import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { fmtCAD, parseDollarsToCents } from "../lib/cataley";
import { toast } from "sonner";
import {
  Sprout, Link2, RefreshCw, Plus, Check, HandCoins, Trash2, PiggyBank,
  Target, History, Settings, ArrowRightLeft, ShoppingBag, Copy, Star, Gift,
} from "lucide-react";
import RewardsTab from "../components/cataley/RewardsTab";

const POT_META = {
  aujourdhui: { label: "Aujourd'hui", color: "#f59e0b", emoji: "🌞" },
  projets: { label: "Mes projets", color: "#6366f1", emoji: "🌱" },
  partager: { label: "Partager", color: "#ec4899", emoji: "💝" },
};

const TABS = [
  { id: "bord", label: "Tableau de bord", icon: Sprout },
  { id: "dojo", label: "Aide au dojo", icon: HandCoins },
  { id: "pots", label: "Mes pots", icon: PiggyBank },
  { id: "reves", label: "Mes rêves", icon: Target },
  { id: "cadeaux", label: "Récompenses", icon: Gift },
  { id: "histo", label: "Historique", icon: History },
  { id: "reglages", label: "Réglages", icon: Settings },
];

function Field({ label, children }) {
  return (
    <label className="block space-y-1">
      <span className="text-sm font-medium text-stone-600">{label}</span>
      {children}
    </label>
  );
}

const inputCls = "w-full rounded-lg border border-stone-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none";
const btn = "inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-50";

export default function CataleyModule() {
  const [tab, setTab] = useState("bord");
  const [st, setSt] = useState(null);
  const [parts, setParts] = useState([]);
  const [goals, setGoals] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);

  async function loadAll() {
    try {
      const [s, p, g, l] = await Promise.all([
        api.get("/cataley/state"),
        api.get("/cataley/participations"),
        api.get("/cataley/goals"),
        api.get("/cataley/ledger"),
      ]);
      setSt(s.data); setParts(p.data.items || []); setGoals(g.data.items || []); setLedger(l.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally { setLoading(false); }
  }
  useEffect(() => { loadAll(); }, []);

  const refreshState = async () => {
    const s = await api.get("/cataley/state"); setSt(s.data);
    const g = await api.get("/cataley/goals"); setGoals(g.data.items || []);
    const l = await api.get("/cataley/ledger"); setLedger(l.data.items || []);
  };

  if (loading) return <div className="p-8 text-stone-500" data-testid="cataley-loading">Chargement…</div>;
  if (!st) return <div className="p-8 text-red-600">Espace indisponible.</div>;

  const childLink = `${window.location.origin}/cataley/${st.child_token}`;

  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6" data-testid="cataley-parent-module">
      {/* En-tête */}
      <div className="rounded-2xl bg-gradient-to-br from-emerald-500 via-teal-500 to-indigo-500 p-6 text-white shadow-lg">
        <div className="flex items-center gap-3">
          <div className="grid h-12 w-12 place-items-center rounded-xl bg-white/20 text-2xl">💎</div>
          <div>
            <h1 className="text-2xl font-black leading-tight" data-testid="cataley-title">Le Trésor de Cataleya Capital</h1>
            <p className="text-sm text-white/90">{st.child_name} · {st.subtitle}</p>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <input readOnly value={childLink} className="min-w-[220px] flex-1 rounded-lg bg-white/15 px-3 py-2 text-xs text-white placeholder-white/70" data-testid="cataley-child-link" />
          <button className={`${btn} bg-white text-indigo-700 hover:bg-white/90`} data-testid="cataley-copy-link"
            onClick={() => { navigator.clipboard?.writeText(childLink); toast.success("Lien copié — ouvre-le avec Cataleya 💛"); }}>
            <Copy size={16} /> Copier le lien enfant
          </button>
          <a href={childLink} target="_blank" rel="noreferrer" className={`${btn} bg-white/20 text-white hover:bg-white/30`} data-testid="cataley-open-child">
            <Link2 size={16} /> Ouvrir son espace
          </a>
          <button className={`${btn} bg-white/20 text-white hover:bg-white/30`} data-testid="cataley-reset-token"
            onClick={async () => { await api.post("/cataley/state/reset-token"); await refreshState(); toast.success("Nouveau lien généré"); }}>
            <RefreshCw size={16} /> Nouveau lien
          </button>
        </div>
      </div>

      {/* Onglets */}
      <div className="mt-4 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`cataley-tab-${t.id}`}
            className={`${btn} ${tab === t.id ? "bg-indigo-600 text-white" : "bg-white text-stone-700 border border-stone-200 hover:bg-stone-50"}`}>
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      <div className="mt-5">
        {tab === "bord" && <BoardTab st={st} parts={parts} goals={goals} />}
        {tab === "dojo" && <DojoTab parts={parts} reload={async () => { const p = await api.get("/cataley/participations"); setParts(p.data.items || []); await refreshState(); }} />}
        {tab === "pots" && <PotsTab st={st} refresh={refreshState} />}
        {tab === "reves" && <GoalsTab st={st} goals={goals} refresh={refreshState} />}
        {tab === "cadeaux" && <RewardsTab refresh={refreshState} />}
        {tab === "histo" && <HistoTab ledger={ledger} />}
        {tab === "reglages" && <SettingsTab st={st} onSaved={refreshState} />}
      </div>
    </div>
  );
}

function Stat({ label, value, color, emoji, testid }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-4" data-testid={testid}>
      <div className="text-xs font-medium text-stone-500">{emoji} {label}</div>
      <div className="mt-1 text-2xl font-black" style={{ color }}>{value}</div>
    </div>
  );
}

function BoardTab({ st, parts, goals }) {
  const prevu = parts.filter((p) => p.status === "prevu").length;
  const aRecevoir = parts.filter((p) => p.status === "confirme" && p.payment_status !== "recu").length;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Argent reçu (total)" value={fmtCAD(st.received_cents)} color="#059669" emoji="💰" testid="cataley-stat-received" />
        <Stat label="Disponible" value={fmtCAD(st.available_cents)} color="#2563eb" emoji="✨" testid="cataley-stat-available" />
        <Stat label="À répartir" value={fmtCAD(st.unallocated_cents)} color="#d97706" emoji="📦" testid="cataley-stat-unalloc" />
        <Stat label="Réservé aux rêves" value={fmtCAD(st.reserved_cents)} color="#6366f1" emoji="🎯" testid="cataley-stat-reserved" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        {Object.entries(POT_META).map(([k, m]) => (
          <Stat key={k} label={m.label} value={fmtCAD(st.pots[k])} color={m.color} emoji={m.emoji} testid={`cataley-pot-${k}`} />
        ))}
      </div>
      <div className="rounded-xl border border-stone-200 bg-white p-4 text-sm text-stone-600">
        <strong>{prevu}</strong> participation(s) prévue(s) · <strong>{aRecevoir}</strong> en attente de versement · <strong>{goals.length}</strong> rêve(s) en cours.
        <p className="mt-2 text-xs text-stone-500">Rappel : l'argent n'est jamais compté avant que tu confirmes qu'il a été réellement remis à Cataleya.</p>
      </div>
    </div>
  );
}

function DojoTab({ parts, reload }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [desc, setDesc] = useState("Aide à papa pendant le cours");
  const [dur, setDur] = useState(30);
  const [amount, setAmount] = useState("2,50");

  const add = async () => {
    const cents = parseDollarsToCents(amount);
    if (cents === null) return toast.error("Montant invalide");
    try {
      await api.post("/cataley/participations", { date, description: desc, duration_min: Number(dur), planned_cents: cents });
      toast.success("Participation ajoutée (prévu)");
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); await reload(); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-stone-200 bg-white p-4">
        <h3 className="mb-3 font-bold text-stone-700">Ajouter une participation</h3>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Field label="Date"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputCls} data-testid="cataley-part-date" /></Field>
          <Field label="Durée (min)"><input type="number" value={dur} onChange={(e) => setDur(e.target.value)} className={inputCls} data-testid="cataley-part-dur" /></Field>
          <Field label="Montant prévu ($)"><input value={amount} onChange={(e) => setAmount(e.target.value)} className={inputCls} data-testid="cataley-part-amount" /></Field>
          <Field label="Description"><input value={desc} onChange={(e) => setDesc(e.target.value)} className={inputCls} data-testid="cataley-part-desc" /></Field>
        </div>
        <button className={`${btn} mt-3 bg-emerald-600 text-white hover:bg-emerald-700`} onClick={add} data-testid="cataley-part-add">
          <Plus size={16} /> Ajouter (prévu)
        </button>
      </div>

      <div className="space-y-2">
        {parts.length === 0 && <p className="text-sm text-stone-500">Aucune participation pour l'instant.</p>}
        {parts.map((p) => {
          const recu = p.payment_status === "recu";
          return (
            <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-stone-200 bg-white p-3" data-testid={`cataley-part-${p.id}`}>
              <div className="min-w-0">
                <div className="font-semibold text-stone-800">{p.date} · {p.description}</div>
                <div className="text-xs text-stone-500">
                  {p.duration_min} min · prévu {fmtCAD(p.planned_cents)}
                  {" · "}
                  <span className={recu ? "text-emerald-600 font-semibold" : p.status === "confirme" ? "text-amber-600" : "text-stone-500"}>
                    {recu ? `reçu ${fmtCAD(p.confirmed_cents)} ✓` : p.status === "confirme" ? `confirmé ${fmtCAD(p.confirmed_cents)} — en attente de versement` : "prévu"}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {p.status === "prevu" && (
                  <button className={`${btn} bg-amber-100 text-amber-800 hover:bg-amber-200`} data-testid={`cataley-part-confirm-${p.id}`}
                    onClick={() => act(() => api.patch(`/cataley/participations/${p.id}`, { status: "confirme" }), "Participation confirmée")}>
                    <Check size={15} /> Confirmer
                  </button>
                )}
                {!recu && p.status === "confirme" && (
                  <button className={`${btn} bg-emerald-600 text-white hover:bg-emerald-700`} data-testid={`cataley-part-receive-${p.id}`}
                    onClick={() => act(() => api.post(`/cataley/participations/${p.id}/receive`), "Versement enregistré 💰")}>
                    <HandCoins size={15} /> Argent reçu
                  </button>
                )}
                {recu && (
                  <button className={`${btn} bg-stone-100 text-stone-600 hover:bg-stone-200`} data-testid={`cataley-part-unreceive-${p.id}`}
                    onClick={() => act(() => api.post(`/cataley/participations/${p.id}/unreceive`), "Correction enregistrée")}>
                    <RefreshCw size={15} /> Corriger
                  </button>
                )}
                {!recu && (
                  <button className={`${btn} bg-rose-50 text-rose-600 hover:bg-rose-100`} data-testid={`cataley-part-del-${p.id}`}
                    onClick={() => act(() => api.delete(`/cataley/participations/${p.id}`), "Supprimé")}>
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PotsTab({ st, refresh }) {
  const [allocPot, setAllocPot] = useState("aujourdhui");
  const [allocAmt, setAllocAmt] = useState("");
  const [tf, setTf] = useState("aujourdhui");
  const [tt, setTt] = useState("projets");
  const [tAmt, setTAmt] = useState("");
  const [spPot, setSpPot] = useState("aujourdhui");
  const [spAmt, setSpAmt] = useState("");
  const [spDet, setSpDet] = useState("");

  const call = async (p, body, msg, reset) => {
    const cents = parseDollarsToCents(body.amount);
    if (cents === null) return toast.error("Montant invalide");
    try { await api.post(p, { ...body.payload, amount_cents: cents }); toast.success(msg); reset && reset(); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {Object.entries(POT_META).map(([k, m]) => (
          <div key={k} className="rounded-xl border-2 bg-white p-4 text-center" style={{ borderColor: m.color }} data-testid={`cataley-potcard-${k}`}>
            <div className="text-3xl">{m.emoji}</div>
            <div className="text-sm font-semibold text-stone-600">{m.label}</div>
            <div className="text-xl font-black" style={{ color: m.color }}>{fmtCAD(st.pots[k])}</div>
          </div>
        ))}
      </div>
      <div className="rounded-xl bg-amber-50 p-3 text-center text-sm text-amber-800">À répartir : <strong>{fmtCAD(st.unallocated_cents)}</strong></div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-stone-200 bg-white p-4">
          <h4 className="mb-2 font-bold text-stone-700">Répartir</h4>
          <select value={allocPot} onChange={(e) => setAllocPot(e.target.value)} className={inputCls} data-testid="cataley-alloc-pot">
            {Object.entries(POT_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
          <input placeholder="Montant $" value={allocAmt} onChange={(e) => setAllocAmt(e.target.value)} className={`${inputCls} mt-2`} data-testid="cataley-alloc-amt" />
          <button className={`${btn} mt-2 w-full justify-center bg-emerald-600 text-white hover:bg-emerald-700`} data-testid="cataley-alloc-btn"
            onClick={() => call("/cataley/allocate", { amount: allocAmt, payload: { pot: allocPot } }, "Argent réparti", () => setAllocAmt(""))}>
            <Plus size={15} /> Mettre dans le pot
          </button>
        </div>

        <div className="rounded-xl border border-stone-200 bg-white p-4">
          <h4 className="mb-2 font-bold text-stone-700">Transférer</h4>
          <div className="flex items-center gap-1">
            <select value={tf} onChange={(e) => setTf(e.target.value)} className={inputCls} data-testid="cataley-tf-from">
              <option value="unallocated">À répartir</option>
              {Object.entries(POT_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>
            <ArrowRightLeft size={16} className="shrink-0 text-stone-400" />
            <select value={tt} onChange={(e) => setTt(e.target.value)} className={inputCls} data-testid="cataley-tf-to">
              <option value="unallocated">À répartir</option>
              {Object.entries(POT_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
            </select>
          </div>
          <input placeholder="Montant $" value={tAmt} onChange={(e) => setTAmt(e.target.value)} className={`${inputCls} mt-2`} data-testid="cataley-tf-amt" />
          <button className={`${btn} mt-2 w-full justify-center bg-indigo-600 text-white hover:bg-indigo-700`} data-testid="cataley-tf-btn"
            onClick={() => call("/cataley/transfer", { amount: tAmt, payload: { from_pot: tf, to_pot: tt } }, "Transfert effectué", () => setTAmt(""))}>
            <ArrowRightLeft size={15} /> Transférer
          </button>
        </div>

        <div className="rounded-xl border border-stone-200 bg-white p-4">
          <h4 className="mb-2 font-bold text-stone-700">Dépenser</h4>
          <select value={spPot} onChange={(e) => setSpPot(e.target.value)} className={inputCls} data-testid="cataley-spend-pot">
            {Object.entries(POT_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
          <input placeholder="Pourquoi ?" value={spDet} onChange={(e) => setSpDet(e.target.value)} className={`${inputCls} mt-2`} data-testid="cataley-spend-det" />
          <input placeholder="Montant $" value={spAmt} onChange={(e) => setSpAmt(e.target.value)} className={`${inputCls} mt-2`} data-testid="cataley-spend-amt" />
          <button className={`${btn} mt-2 w-full justify-center bg-rose-500 text-white hover:bg-rose-600`} data-testid="cataley-spend-btn"
            onClick={() => call("/cataley/spend", { amount: spAmt, payload: { pot: spPot, detail: spDet || "Dépense" } }, "Dépense enregistrée", () => { setSpAmt(""); setSpDet(""); })}>
            <ShoppingBag size={15} /> Dépenser
          </button>
        </div>
      </div>
    </div>
  );
}

function GoalsTab({ st, goals, refresh }) {
  const [title, setTitle] = useState("");
  const [price, setPrice] = useState("");
  const [img, setImg] = useState("");
  const [resAmt, setResAmt] = useState({});

  const add = async () => {
    const cents = parseDollarsToCents(price);
    if (!title.trim() || cents === null || cents <= 0) return toast.error("Titre et prix requis");
    try { await api.post("/cataley/goals", { title, target_cents: cents, image_url: img }); toast.success("Rêve créé 🎯"); setTitle(""); setPrice(""); setImg(""); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const reserve = async (g) => {
    const cents = parseDollarsToCents(resAmt[g.id]);
    if (cents === null) return toast.error("Montant invalide");
    try { await api.post(`/cataley/goals/${g.id}/reserve`, { amount_cents: cents }); toast.success("Réservation mise à jour"); await refresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async (g) => { try { await api.delete(`/cataley/goals/${g.id}`); toast.success("Rêve supprimé"); await refresh(); } catch (e) { toast.error("Erreur"); } };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-stone-200 bg-white p-4">
        <h3 className="mb-3 font-bold text-stone-700">Nouveau rêve</h3>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Titre"><input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} data-testid="cataley-goal-title" placeholder="Ex : Jeu de Lego" /></Field>
          <Field label="Prix cible ($)"><input value={price} onChange={(e) => setPrice(e.target.value)} className={inputCls} data-testid="cataley-goal-price" placeholder="20,00" /></Field>
          <Field label="Image (URL, optionnel)"><input value={img} onChange={(e) => setImg(e.target.value)} className={inputCls} data-testid="cataley-goal-img" /></Field>
        </div>
        <button className={`${btn} mt-3 bg-indigo-600 text-white hover:bg-indigo-700`} onClick={add} data-testid="cataley-goal-add"><Plus size={16} /> Créer le rêve</button>
        <p className="mt-2 text-xs text-stone-500">La réservation prend l'argent dans le pot « Mes projets » ({fmtCAD(st.pots.projets)} disponible).</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {goals.length === 0 && <p className="text-sm text-stone-500">Aucun rêve pour l'instant.</p>}
        {goals.map((g) => (
          <div key={g.id} className="rounded-xl border border-stone-200 bg-white p-4" data-testid={`cataley-goal-${g.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-bold text-stone-800">{g.title}</div>
                <div className="text-xs text-stone-500">Cible {fmtCAD(g.target_cents)} · reste {fmtCAD(g.remaining_cents)}</div>
              </div>
              <button className="rounded-lg p-1 text-rose-500 hover:bg-rose-50" onClick={() => del(g)} data-testid={`cataley-goal-del-${g.id}`}><Trash2 size={16} /></button>
            </div>
            <div className="mt-2 h-3 w-full overflow-hidden rounded-full bg-stone-100">
              <div className="h-full rounded-full bg-indigo-500 transition-all" style={{ width: `${g.percent}%` }} />
            </div>
            <div className="mt-1 text-right text-xs font-semibold text-indigo-600">{g.percent}% · {fmtCAD(g.reserved_cents)} réservé</div>
            <div className="mt-2 flex gap-2">
              <input placeholder="Réserver total $" value={resAmt[g.id] || ""} onChange={(e) => setResAmt({ ...resAmt, [g.id]: e.target.value })} className={inputCls} data-testid={`cataley-goal-resamt-${g.id}`} />
              <button className={`${btn} shrink-0 bg-emerald-600 text-white hover:bg-emerald-700`} onClick={() => reserve(g)} data-testid={`cataley-goal-reserve-${g.id}`}><Target size={15} /> Réserver</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const LEDGER_LABELS = { receive: "Argent reçu", allocate: "Répartition", transfer: "Transfert", spend: "Dépense", reserve: "Réservation", correction: "Correction", goal_delete: "Rêve supprimé" };
function HistoTab({ ledger }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-2" data-testid="cataley-ledger">
      {ledger.length === 0 && <p className="p-3 text-sm text-stone-500">Aucun mouvement pour l'instant.</p>}
      {ledger.map((l) => (
        <div key={l.id} className="flex items-center justify-between border-b border-stone-100 px-3 py-2 last:border-0">
          <div>
            <div className="text-sm font-medium text-stone-700">{LEDGER_LABELS[l.type] || l.type}</div>
            <div className="text-xs text-stone-500">{l.detail} · {String(l.created_at).slice(0, 16).replace("T", " ")}</div>
          </div>
          <div className={`text-sm font-bold ${l.amount_cents < 0 ? "text-rose-600" : "text-emerald-600"}`}>
            {l.amount_cents !== 0 ? (l.amount_cents < 0 ? "−" : "+") + fmtCAD(Math.abs(l.amount_cents)) : "—"}
          </div>
        </div>
      ))}
    </div>
  );
}

function SettingsTab({ st, onSaved }) {
  const [f, setF] = useState({
    child_name: st.child_name, subtitle: st.subtitle, parent_name: st.parent_name,
    parent_message: st.parent_message,
    difficulty: st.settings?.difficulty || "facile",
    animations: st.settings?.animations !== false,
    partager_enabled: st.settings?.partager_enabled !== false,
  });
  const save = async () => {
    try {
      await api.put("/cataley/state", {
        child_name: f.child_name, subtitle: f.subtitle, parent_name: f.parent_name, parent_message: f.parent_message,
        settings: { difficulty: f.difficulty, animations: f.animations, partager_enabled: f.partager_enabled },
      });
      toast.success("Réglages enregistrés"); onSaved && onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="max-w-xl space-y-3 rounded-xl border border-stone-200 bg-white p-4" data-testid="cataley-settings">
      <Field label="Prénom de l'enfant"><input value={f.child_name} onChange={(e) => setF({ ...f, child_name: e.target.value })} className={inputCls} data-testid="cataley-set-name" /></Field>
      <Field label="Sous-titre"><input value={f.subtitle} onChange={(e) => setF({ ...f, subtitle: e.target.value })} className={inputCls} data-testid="cataley-set-sub" /></Field>
      <Field label="Nom du parent"><input value={f.parent_name} onChange={(e) => setF({ ...f, parent_name: e.target.value })} className={inputCls} data-testid="cataley-set-parent" /></Field>
      <Field label="Message de papa (affiché dans son jardin)"><textarea value={f.parent_message} onChange={(e) => setF({ ...f, parent_message: e.target.value })} className={inputCls} rows={2} data-testid="cataley-set-msg" /></Field>
      <Field label="Difficulté des jeux">
        <select value={f.difficulty} onChange={(e) => setF({ ...f, difficulty: e.target.value })} className={inputCls} data-testid="cataley-set-diff">
          <option value="facile">Facile</option><option value="moyen">Moyen</option><option value="difficile">Difficile</option>
        </select>
      </Field>
      <label className="flex items-center gap-2 text-sm text-stone-600"><input type="checkbox" checked={f.animations} onChange={(e) => setF({ ...f, animations: e.target.checked })} data-testid="cataley-set-anim" /> Animations</label>
      <label className="flex items-center gap-2 text-sm text-stone-600"><input type="checkbox" checked={f.partager_enabled} onChange={(e) => setF({ ...f, partager_enabled: e.target.checked })} data-testid="cataley-set-partager" /> Afficher le pot « Partager »</label>
      <button className={`${btn} bg-indigo-600 text-white hover:bg-indigo-700`} onClick={save} data-testid="cataley-set-save"><Check size={16} /> Enregistrer</button>
    </div>
  );
}
